import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType, selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { Fragment, useEffect, useMemo, useState } from 'react';

import PortalFXSwapsTable from '@/components/portal-modules/PortalFXSwapsTable';
import { getOperationalCurrentTradeDate, PeriodOption } from '@/helpers/dateHelper';
import { convertToFxSwapsRow, FXSwapRow } from '@/helpers/portalFXSwapsHelper';
import { TableFilter } from '@/types/table';
import Unauthorized401 from '@/Unauthorized401';
import { useFXSwapsFilter } from '@/utils/hooks/useFXSwapsFilter';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { useSso } from '@/utils/providers/SSOProvider';
import { subDays } from 'date-fns';
import { useDateFormatter, useLocale } from 'react-aria';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

const cardHeight = 122;
const tableHeight = 204.2;

export type InstrumentTypeFilter =
    | 'N/A'
    | 'Crypto CFD'
    | 'Equity Index CFD'
    | 'Commodity CFD'
    | 'Rolling Fx'
    | 'Fx Spot'
    | 'Crypto Spot'
    | 'Futures CFD';

export interface FXSwapsFilters {
    startDate: TableFilter<Date>;
    endDate: TableFilter<Date>;
}

const currentTradeDate = getOperationalCurrentTradeDate();

function PortalFXSwaps() {
    usePortalAuthentication();

    const lang = useLanguage();
    const navigate = useNavigate();
    const screen = useScreenSizes();

    const accountType = useAppSelector(selectAccountType);

    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const { instrumentsIncludeRatioApi, hasPermission } = useSso();

    const defaultFilters = useMemo(
        () => ({
            startDate: {
                value: subDays(currentTradeDate, 7),
                label: lang.common.startDate
            },
            endDate: {
                value: currentTradeDate,
                label: lang.common.endDate
            },
            dateRange: {
                value: 'custom',
                label: lang.common.custom
            } as PeriodOption
        }),
        [lang, currentTradeDate]
    );

    const { locale } = useLocale();

    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });

    const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
    const [filters, setFilters] = useState<FXSwapsFilters>(defaultFilters);

    const { startDate, endDate } = useMemo(() => filters, [filters]);

    const {
        data: fxSwaps,
        isLoading: isFXSwapsLoading,
        isValidating: isFXSwapsValidating,
        mutate: refreshAllFXSwaps
    } = useFXSwapsFilter({
        startDate,
        endDate
    });

    const { data: instruments } = instrumentsIncludeRatioApi;

    const fxSwapsData: FXSwapRow[] = useMemo(() => {
        if (fxSwaps) {
            return fxSwaps.map((fxSwaps) => convertToFxSwapsRow(fxSwaps, instruments || [], dateFormatter, locale));
        } else return [];
    }, [fxSwaps, dateFormatter, instruments, locale]);

    useEffect(() => {
        refreshAllFXSwaps();
    }, [filters.startDate, filters.endDate]);

    useEffect(() => {
        if (accountType === 'NOP') {
            navigate('/portal');
        } else {
            refreshAllFXSwaps();
            setIsInitialMount(false);
        }
    }, [currentAccount, accountType]);

    const isAuthorized = hasPermission('ACCOUNT_VIEW');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0 max-h-full">
                    <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                        <div className="font-bold text-xl">{lang.navigation['fx swaps']}</div>
                        {/* <Button
                                type="submit"
                                className="h-fit w-fit !gap-2 text-xs"
                                onClick={exportDisclosure[1].open}>
                                <MdOutlineDocumentScanner className="w-4 h-4" />
                                Export Trades
                            </Button> */}
                    </div>
                    {isInitialMount && (isFXSwapsLoading || isFXSwapsValidating) ? (
                        <Fragment>
                            {screen.mobile ? (
                                <div className="flex flex-col w-full gap-2 sm:gap-4">
                                    <Skeleton containerClassName="flex-1" height={cardHeight} />
                                    {/* <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} /> */}
                                </div>
                            ) : (
                                <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                    {/* <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        </div> */}
                                    {/* <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div> */}
                                    <div className="flex w-full">
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <PortalFXSwapsTable
                            data={fxSwapsData}
                            filters={filters}
                            setFilters={setFilters}
                            defaultFilters={defaultFilters}
                            isLoading={isFXSwapsLoading || isFXSwapsValidating}
                            mutate={refreshAllFXSwaps as any}
                        />
                    )}
                </div>
            )}
            {!isAuthorized && <Unauthorized401 />}
            {/* <ExportTradesModal opened={exportDisclosure[0]} handlers={exportDisclosure[1]} /> */}
        </PortalLayout>
    ) : null;
}

export default PortalFXSwaps;
